/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { Query } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'

import {
  ExpandableOverviewItem,
  LabelledDetail,
  Button,
  TextArea,
  SpanWithData,
  Table,
  LoanIssueOrView
} from '../'

import { ReadOnlyContext } from '../../context'

import {
  showAsCurrency,
  getNodeTypeFromBase64Id
} from '../../utils'

import { WORKFLOW_LOAN_DISBURSAL } from '../../constants'


export default class LoanApplicationIssue extends React.Component {
  static propTypes = {
    issuedBy: PropTypes.string,
    issuerNotes: PropTypes.string,
    approverNotes: PropTypes.string,
    open: PropTypes.bool.isRequired,
    updateLoanApplication: PropTypes.func.isRequired,
    updateLoan: PropTypes.func.isRequired,
    loanApplication: PropTypes.shape({
      id: PropTypes.string.isRequired,
      cashEligibleFor: PropTypes.number,
      retailEligibleFor: PropTypes.number,
      consolidationEligibleFor: PropTypes.number,
      status: PropTypes.bool
    }).isRequired,
    loans: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      loanType: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
      instalmentAgreed: PropTypes.number.isRequired
    })).isRequired,
    onClick: PropTypes.func.isRequired,
    collapseSection: PropTypes.func.isRequired,
    creditApplication: PropTypes.bool,
    className: PropTypes.string
  }

  state = {
    issuerNotes: this.props.issuerNotes || '',
    saveInProgress: false
  }

  handleIssuerNotesChange = (e) => this.setState({ issuerNotes: e.target.value })

  handleCancelClick = () => this.setState({ issuerNotes: this.props.issuerNotes || '' })
  handleCompleteIssueClick = () => {
    let params = {
      issuedBy: localStorage.getItem("userUUID"),
      issuedAt: moment.utc().format("YYYY-MM-DD HH:mm:ss.SSS"),
      status: this.props.creditApplication
        ? "credits_pending_disbursements"
        : "loans_issued",
    };

    if (!this.issuerNotesUnchanged()) {
      params.issuerNotes = this.state.issuerNotes
    }

    this.props.updateLoanApplication(params)
    .then(() => this.setState({ saveInProgress: false }, this.props.collapseSection))
    .catch(() => this.setState({ saveInProgress: false }))
  }

  handleRemoveFinalisationClick = (userName) => {
    // append to approverNotes
    let finaliseRemovedLine = `\n\n=== Finalisation removed by ${userName} at ${moment().format('DD-MM-YYYY HH:mm')} ===\n\n`
    let outcomeNotes = `${this.props.outcomeNotes ? this.props.outcomeNotes : ''}${finaliseRemovedLine}`
    this.props.updateLoanApplication({
      finalisedBy: 'null',
      approvedBy: 'null',
      outcome: null,
      status: 'incomplete',
      outcomeNotes
    })
    .then(this.props.collapseSection)
  }

  issuerNotesUnchanged = () => {
    if (this.props.issuerNotes) {
      return this.state.issuerNotes === this.props.issuerNotes
    } else {
      return this.state.issuerNotes === ''
    }
  }

  getSummary = (issued, approved) => {
    if (issued) {
      return 'Loans issued'
    } else if (!approved) {
      return 'Requires approval'
    }
  }

  getLoanEligibleFor = (loanType) => {
    let { cashEligibleFor, retailEligibleFor, consolidationEligibleFor, amountEligibleFor } = this.props.loanApplication

    return loanType === 'CASH'
      ? cashEligibleFor || amountEligibleFor
      : loanType === 'CONSOLIDATION'
        ? consolidationEligibleFor || amountEligibleFor
        : retailEligibleFor || amountEligibleFor
  }

  render() {
    let { issuedBy, approvedBy, status, open, loans, updateLoan, loanApplication, onClick, className } = this.props
    let issueComplete = issuedBy !== null
    let approvedStatus = status === 'APPROVED'

    return (
      <ExpandableOverviewItem
        title='Issue'
        summary={this.getSummary(issueComplete, approvedBy)}
        iconSymbol={issueComplete ? 'check' : null}
        iconColour={issueComplete ? 'success' : null}
        open={open}
        locked={!approvedBy}
        onClick={onClick}
        className={className}>
        <div className='slds-m-left_small slds-m-top_small slds-m-bottom_medium'>
          <Table
            columns={[ 'type', 'eligible for', 'amount', 'instalment', '']}
            rows={loans.map(loan => {
              let isCredit = getNodeTypeFromBase64Id(loan.id) === 'CreditNode'
              let loanType = isCredit ? loan.creditType : loan.loanType
              return (
                <LoanDetails
                  loanId={loan.id}
                  loanType={loanType}
                  eligibleFor={this.getLoanEligibleFor(loanType)}
                  amount={loan.amount}
                  instalment={loan.instalmentAgreed}
                  workflowRunProcessing={loan.workflowrunProcessing}
                  updateLoan={updateLoan}
                  loanApplicationStatus={loanApplication.status}
                  longtermContributionRequired={loanApplication.securityLtContributionActual}
                  isCredit={isCredit}
                  key={`loanDetails_${loan.id}`}
                />
              )
            })}
            objectRows={true}
            noCellPadding={true}
            className='slds-m-bottom_large'
          />
          <div className='slds-size_3-of-4'>
            <LabelledDetail
              label='Notes'
              detail={
                <TextArea
                  placeholder='Add issuer notes'
                  value={this.state.issuerNotes}
                  onChange={this.handleIssuerNotesChange}
                  rows={5}
                  disabled={issueComplete || !approvedStatus}
                />
              }
              detailFontSize='inherit'
            />
          </div>
          <div className='slds-grid slds-align_absolute-center slds-m-top_large'>
            <div className='slds-col_bump-left slds-m-right_xx-large'>
              <ReadOnlyContext.Consumer>
                {readOnly => (
                  <Fragment>
                    <Query query={gql`query { me { firstName lastName } }`}>
                      {({ data }) => (
                        <Button
                          onClick={() => (
                            data
                              ? this.handleRemoveFinalisationClick(`${data.me.firstName} ${data.me.lastName}`)
                              : false
                          )}
                          disabled={readOnly || !data || issueComplete || this.state.saveInProgress}
                          title='This will reset the finalised and approved steps'
                          className='slds-m-right_large'>
                          Remove finalisation
                        </Button>
                      )}
                    </Query>
                    <Button
                      minWidth={90}
                      onClick={this.handleCancelClick}
                      disabled={this.issuerNotesUnchanged() || this.state.saveInProgress || !approvedStatus}>
                      Cancel
                    </Button>
                    <Button
                      type='brand'
                      minWidth={90}
                      onClick={this.handleCompleteIssueClick}
                      disabled={readOnly || this.issuerNotesUnchanged() || this.state.saveInProgress ||
                        !approvedStatus || loans.find(loan => loan.workflowrunProcessing) === undefined}
                      className='slds-m-left_small'>
                      {this.state.saveInProgress
                        ? 'Saving...'
                        : 'Complete issue'
                      }
                    </Button>
                  </Fragment>
                )}
              </ReadOnlyContext.Consumer>
            </div>
          </div>
        </div>
      </ExpandableOverviewItem>
    )
  }
}

const LoanDetails = ({ loanId, loanType, loanApplicationStatus, eligibleFor, amount, instalment, lmsId, workflowRunProcessing, longtermContributionRequired, updateLoan, isCredit }) => {
  let historicLoanApplication = workflowRunProcessing === null &&
    ('FINALISED_APPLICATION', 'LOANS_DISBURSED', 'LOANS_ACTIVATED').indexOf(loanApplicationStatus) > -1

  return(
    <tr>
      <td>
        <SpanWithData forField={isCredit ? 'CreditCreditType' : 'LoanLoanType'} searchKey={loanType} />
      </td>
      <td>
        {showAsCurrency('R', eligibleFor)}
      </td>
      <td>
        {amount ? showAsCurrency('R', amount) : null}
      </td>
      <td>
        {instalment ? showAsCurrency('R', instalment) : ''}
      </td>
      <td>
        <LoanIssueOrView
          loan={loanId}
          workflow={WORKFLOW_LOAN_DISBURSAL}
          workflowRunProcessing={workflowRunProcessing}
          updateLoan={updateLoan}
          historicLoanApplication={historicLoanApplication}
          applicationApproved={loanApplicationStatus === 'APPROVED'}
          longtermContributionRequired={longtermContributionRequired}
        />
      </td>
    </tr>
  )
}



// WEBPACK FOOTER //
// ./src/components/loanapplication-issue/index.js